import { Haptics, ImpactStyle } from '@capacitor/haptics';
import {
    IonButton,
    IonContent,
    IonIcon,
    IonModal,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonSelect,
    IonSelectOption,
    IonToast,
    RefresherEventDetail
} from '@ionic/react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { addCircle, cloudUpload, lockClosed, star, trendingUp, trophy, videocam } from 'ionicons/icons';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { Events, Params, Screens, useAnalytics } from '../AppAnalyticsProvider';
import { useAppState } from '../AppListenerProvider';
import { AppContext } from '../AppStateProvider';
import CustomHelmet from '../components/ComponentsFunctional/CustomHelmet/CustomHelmet';
import SpiderChart from '../components/ComponentsStats/SpiderChart';
import StatsSummaryCard from '../components/ComponentsStats/StatsSummaryCard';
import SwipableStatsCard from '../components/ComponentsStats/SwipableStatsCard';
import UserComparisonSelector from '../components/ComponentsStats/UserComparisonSelector';
import { WeeklyRating } from '../components/ComponentsStats/WeeklyRatingChart';
import StatsLoadingSkeleton from '../components/ComponentsUI/StatsLoadingSkeleton/StatsLoadingSkeleton';
import ToolbarSearchBarStats from '../components/ComponentsUI/ToolbarSearchBarStats/ToolbarSearchBarStats';
import { PRODUCT_NAMES, useRevenueCatPaywall } from '../hooks/useRevenueCatPaywall';
import { HotType } from '../models/hotType';
import { SportType } from '../models/sportType';
import { StatsUserInfo, UserStatsApiResponse, WeeklyHistoricalRating } from '../models/userStats';
import PreferencesService from '../services/preferences.service';
import StatsService, { transformApiResponse } from '../services/stats.service';
import TricksService from '../services/tricks.service';

const StatsPage: React.FC = () => {
    const history = useHistory();
    const [selectedPeriod, setSelectedPeriod] = useState<HotType>(HotType.All);
    const [selectedSport, setSelectedSport] = useState<SportType>(SportType.SKIING);
    const [comparisonUsers, setComparisonUsers] = useState<StatsUserInfo[]>([]);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showComparisonModal, setShowComparisonModal] = useState(false);
    const { state } = useContext(AppContext);
    const { isNative } = useAppState();
    const { trackScreen, trackEvent } = useAnalytics();

    // Paywall integration
    const {
        presentPaywall: presentTowRopePaywall,
    } = useRevenueCatPaywall({
        entitlementIdentifier: 'pro',
        trackAnalytics: true,
        productName: PRODUCT_NAMES.TOW_ROPE_PASS,
        userId: state.user.id,
        userBisk: state.bisk,
        username: state.user.username
    });

    // Colors for the spider chart using brand colors
    const chartColors = [
        '#5861AC', // highlight - primary user
        '#ff6ad5', // neon-pink - comparison user 1
        '#8FBC8F', // highlight-secondary - comparison user 2
        '#0affff', // electric-blue - comparison user 3
        '#324E80', // highlight-alt - comparison user 4
    ];

    // Load saved preferences on mount
    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const savedSport = await PreferencesService.getStatsSport();
                if (savedSport) {
                    console.log("Using saved sport:", savedSport);
                    setSelectedSport(savedSport as SportType);
                } else {
                    console.log("No saved sport, checking user profile sports:", state.user?.profile?.sports);
                    if (state.user?.profile?.sports) {
                        if (state.user.profile.sports.length === 1) {
                            console.log("Setting sport from user profile:", state.user.profile.sports[0]);
                            setSelectedSport(state.user.profile.sports[0] as SportType);
                        } else {
                            console.log("Multiple sports found, defaulting to first sport");
                            setSelectedSport(state.user.profile.sports[0] as SportType);
                        }
                    } else {
                        console.log("No user profile sports, defaulting to skiing");
                        setSelectedSport(SportType.SKIING);
                    }
                }
            } catch (error) {
                console.error('Error loading preferences:', error);
            }
        };

        // Only run if we have a user
        if (state.user?.id) {
            loadPreferences();
        }
    }, [state.user?.id, state.user?.profile?.sports]);

    useEffect(() => {
        const loadPreferencesPeriod = async () => {
            try {
                const savedPeriod = await PreferencesService.getStatsPeriod();
                if (savedPeriod) {
                    setSelectedPeriod(savedPeriod as HotType);
                }
            } catch (error) {
                console.error('Error loading preferences:', error);
            }
        };
        loadPreferencesPeriod();
    }, []);

    // Track screen view when Stats page loads
    useEffect(() => {
        trackScreen(Screens.STATS);
    }, []);

    // Fetch raw user stats - cached by user_id only (not period/sport)
    // This way changing period/sport won't trigger a new API call
    const { data: rawUserStats, isLoading: primaryLoading, refetch: refetchPrimary } = useQuery<UserStatsApiResponse | null>({
        queryKey: ['userStatsRaw', state.user.id],
        queryFn: async () => {
            if (!state.user.id) return null;
            return await StatsService.getUserStatsRaw(state.user.id);
        },
        enabled: !!state.user.id,
        staleTime: 15 * 60 * 1000, // 15 minutes - matches the stats update interval
    });

    // Fetch weekly historical ratings for the line chart
    const { data: weeklyHistoricalData, refetch: refetchWeekly } = useQuery<WeeklyHistoricalRating[]>({
        queryKey: ['weeklyHistoricalRatings', state.user.id, selectedSport],
        queryFn: async () => {
            if (!state.user.id) return [];
            return await StatsService.getWeeklyHistoricalRatings(state.user.id, selectedSport);
        },
        enabled: !!state.user.id,
        staleTime: 15 * 60 * 1000, // 15 minutes
    });

    // Fetch user tricks - this is already cached across the app (Profile, UploadSuccess, etc.)
    // Using this instead of calculating from stats gives us real-time clip count
    const { data: userTricks, refetch: refetchUserTricks } = useQuery({
        queryKey: ['userTricks', state.user.id],
        queryFn: () => TricksService.getUserTricks(state.user.id),
        enabled: !!state.user.id,
        staleTime: 5 * 60 * 1000, // 5 minutes - more responsive than stats
    });

    // Number of clips before stats paywall kicks in - optimized for conversions
    // Users can always upload unlimited clips for free
    // But viewing detailed stats requires subscription after 5 clips
    const FREE_STATS_THRESHOLD = 5;

    // Use userTricks.length for clip count - this is already cached and more accurate than stats
    const totalClipCount = userTricks?.length ?? 0;

    // Users get full stats access until they hit the threshold
    // After 5 clips, they need a subscription to VIEW their stats (uploading is always free)
    const hasFullStatsAccess = state.isSubscribed || totalClipCount < FREE_STATS_THRESHOLD;

    // Transform weekly historical data for the chart
    const weeklyChartData = useMemo((): WeeklyRating[] => {
        if (!weeklyHistoricalData || weeklyHistoricalData.length === 0) return [];

        return weeklyHistoricalData.map(item => ({
            week: format(new Date(item.week_start), 'MMM d'),
            weekNumber: item.week_number,
            rating: item.overall_rating,
            date: new Date(item.week_start)
        }));
    }, [weeklyHistoricalData]);

    // Transform the cached raw stats based on selected period and sport
    // This is computed client-side without additional API calls
    const primaryUserStats = useMemo(() => {
        if (!rawUserStats) return null;
        return transformApiResponse(
            rawUserStats,
            selectedPeriod,
            selectedSport,
            state.user.username,
            state.user.profile?.profile_pic
        );
    }, [rawUserStats, selectedPeriod, selectedSport, state.user.username, state.user.profile?.profile_pic]);

    // Fetch raw stats for comparison users - cached by user IDs only
    // Changing period/sport will transform cached data client-side
    const comparisonUserIds = useMemo(() => comparisonUsers.map(u => u.id), [comparisonUsers]);

    const { data: rawComparisonStats, isLoading: comparisonLoading, refetch: refetchComparison } = useQuery<Map<string, UserStatsApiResponse>>({
        queryKey: ['comparisonStatsRaw', comparisonUserIds],
        queryFn: async () => {
            if (comparisonUserIds.length === 0) return new Map();
            return await StatsService.getComparisonUsersStatsRaw(comparisonUserIds);
        },
        enabled: comparisonUserIds.length > 0,
        staleTime: 15 * 60 * 1000, // 15 minutes
    });

    // Transform comparison users' cached raw stats based on selected period and sport
    const comparisonStats = useMemo(() => {
        if (!rawComparisonStats || rawComparisonStats.size === 0) return null;

        const comparison_users = comparisonUsers
            .map(user => {
                const rawStats = rawComparisonStats.get(user.id);
                if (!rawStats) return null;
                return transformApiResponse(
                    rawStats,
                    selectedPeriod,
                    selectedSport,
                    user.username,
                    user.profile_pic
                );
            })
            .filter((stats): stats is NonNullable<typeof stats> => stats !== null);

        return {
            comparison_users,
            period: selectedPeriod,
            sport: selectedSport
        };
    }, [rawComparisonStats, comparisonUsers, selectedPeriod, selectedSport]);

    const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
        if (isNative) {
            await Haptics.impact({ style: ImpactStyle.Medium });
        }
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: 'refresh',
            [Params.SCREEN_NAME]: Screens.STATS
        });
        await Promise.all([refetchPrimary(), refetchComparison(), refetchWeekly(), refetchUserTricks()]);
        event.detail.complete();
    };

    const handlePeriodChange = async (period: HotType) => {
        if (isNative) {
            await Haptics.impact({ style: ImpactStyle.Light });
        }
        trackEvent(Events.FILTER_CHANGE, {
            [Params.FILTER_TYPE]: 'period',
            [Params.FILTER_VALUE]: period,
            [Params.TIME_RANGE]: period,
            [Params.SCREEN_NAME]: Screens.STATS
        });
        setSelectedPeriod(period);
        await PreferencesService.setStatsPeriod(period);
    };

    const handleSportChange = async (sport: SportType) => {
        if (isNative) {
            await Haptics.impact({ style: ImpactStyle.Light });
        }
        trackEvent(Events.FILTER_CHANGE, {
            [Params.FILTER_TYPE]: 'sport',
            [Params.FILTER_VALUE]: sport,
            [Params.SPORT]: sport,
            [Params.SCREEN_NAME]: Screens.STATS
        });
        setSelectedSport(sport);
        await PreferencesService.setStatsSport(sport);
    };

    const handleComparisonUsersChange = (users: StatsUserInfo[]) => {
        if (isNative && users.length !== comparisonUsers.length) {
            Haptics.impact({ style: ImpactStyle.Light });
        }
        trackEvent(Events.SEGMENT_CHANGE, {
            [Params.SEGMENT_NAME]: 'comparison_users',
            [Params.ITEM_COUNT]: users.length,
            [Params.SCREEN_NAME]: Screens.STATS
        });
        setComparisonUsers(users);
    };

    const handleOpenComparisonModal = async () => {
        if (state.isSubscribed) {
            if (isNative) {
                await Haptics.impact({ style: ImpactStyle.Light });
            }
            trackEvent(Events.MODAL_VIEW, {
                [Params.MODAL_NAME]: 'comparison_selector',
                [Params.SCREEN_NAME]: Screens.STATS
            });
            setShowComparisonModal(true);
        } else {
            if (isNative) {
                await Haptics.impact({ style: ImpactStyle.Medium });
            }
            trackEvent(Events.BUTTON_CLICK, {
                [Params.BUTTON_NAME]: 'comparison_paywall',
                [Params.SCREEN_NAME]: Screens.STATS
            });
            presentTowRopePaywall();
        }
    };

    const handleCloseComparisonModal = () => {
        if (isNative) {
            Haptics.impact({ style: ImpactStyle.Light });
        }
        trackEvent(Events.MODAL_CLOSE, {
            [Params.MODAL_NAME]: 'comparison_selector',
            [Params.SCREEN_NAME]: Screens.STATS
        });
        setShowComparisonModal(false);
    };

    const handlePaywallClick = async () => {
        if (isNative) {
            await Haptics.impact({ style: ImpactStyle.Medium });
        }
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: 'stats_paywall',
            [Params.SCREEN_NAME]: Screens.STATS
        });
        presentTowRopePaywall();
    };

    const handleUploadClick = async () => {
        if (isNative) {
            await Haptics.impact({ style: ImpactStyle.Medium });
        }
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: 'stats_upload_cta',
            [Params.SCREEN_NAME]: Screens.STATS
        });
    };

    const handleLockedStatClick = async () => {
        if (isNative) {
            await Haptics.impact({ style: ImpactStyle.Medium });
        }
        trackEvent(Events.BUTTON_CLICK, {
            [Params.BUTTON_NAME]: 'locked_stat_category',
            [Params.SCREEN_NAME]: Screens.STATS
        });
        presentTowRopePaywall();
    };

    // Prepare chart data
    const chartData = () => {
        if (!primaryUserStats) return [];

        const data = [{
            username: primaryUserStats.username,
            stats: primaryUserStats.stats,
            color: chartColors[0]
        }];

        if (comparisonStats?.comparison_users) {
            comparisonStats.comparison_users.forEach((user, index) => {
                data.push({
                    username: user.username,
                    stats: user.stats,
                    color: chartColors[index + 1] || chartColors[chartColors.length - 1]
                });
            });
        }

        return data;
    };

    const isLoading = primaryLoading;

    // Paywall Overlay Component
    const PaywallOverlay: React.FC<{ children: React.ReactNode; className?: string; showPaywall?: boolean }> = ({
        children,
        className = "",
        showPaywall = true
    }) => {
        // If user has full stats access (subscribed OR under free threshold), show content directly
        if (hasFullStatsAccess || !showPaywall) {
            return <>{children}</>;
        }

        // If user has 0 clips, show "Upload a Clip" prompt instead of paywall
        if (totalClipCount === 0) {
            return (
                <div className={`relative ${className}`}>
                    {/* Blurred Content */}
                    <div className="blur-md pointer-events-none">
                        {children}
                    </div>

                    {/* Upload Clip Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent flex flex-col justify-center items-center p-6 text-center">
                        <div className="bg-gradient-to-br from-[#8FBC8F] to-[#5861AC] p-1 rounded-2xl shadow-2xl max-w-sm w-full">
                            <div className="bg-black/90 rounded-xl p-6">
                                <IonIcon icon={videocam} className="text-4xl text-[#8FBC8F] mb-4" />

                                <h3 className="text-white font-bold text-xl mb-2">
                                    🎬 Upload Your First Clip!
                                </h3>

                                <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                                    Upload a clip of your riding and let Lifty AI analyze your skills! Get personalized stats and see how you stack up.
                                </p>

                                <div className="flex flex-col gap-2 mb-4 text-xs">
                                    <div className="flex items-center text-[#8FBC8F]">
                                        <IonIcon icon={cloudUpload} className="mr-2" />
                                        <span>Upload any ski or snowboard clip</span>
                                    </div>
                                    <div className="flex items-center text-[#0affff]">
                                        <IonIcon icon={star} className="mr-2" />
                                        <span>Get AI-powered skill ratings</span>
                                    </div>
                                    <div className="flex items-center text-[#ff6ad5]">
                                        <IonIcon icon={trendingUp} className="mr-2" />
                                        <span>Track your progression over time</span>
                                    </div>
                                </div>

                                <IonButton
                                    onClick={handleUploadClick}
                                    className="w-full"
                                    style={{
                                        '--background': 'linear-gradient(135deg, #8FBC8F 0%, #5861AC 100%)',
                                        '--border-radius': '12px',
                                        '--box-shadow': '0 4px 20px rgba(143, 188, 143, 0.4)',
                                        height: '48px',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Upload a Clip
                                </IonButton>

                                <p className="text-gray-500 text-xs mt-3">
                                    Start building your stats today
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // User has >= threshold clips but not subscribed - show paywall
        return (
            <div className={`relative ${className}`}>
                {/* Blurred Content */}
                <div className="blur-md pointer-events-none">
                    {children}
                </div>

                {/* Paywall Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent flex flex-col justify-center items-center p-6 text-center">
                    <div className="bg-gradient-to-br from-[#5861AC] to-[#324E80] p-1 rounded-2xl shadow-2xl max-w-sm w-full">
                        <div className="bg-black/90 rounded-xl p-6">
                            <IonIcon icon={lockClosed} className="text-4xl text-[#5861AC] mb-4" />

                            <h3 className="text-white font-bold text-xl mb-2">
                                🔓 Unlock Your Full Potential
                            </h3>

                            <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                                You've uploaded {totalClipCount} clips! Keep uploading for free - but unlock your detailed stats breakdown with Tow Rope Pass.
                            </p>

                            <div className="flex flex-col gap-2 mb-4 text-xs">
                                <div className="flex items-center text-[#ff6ad5]">
                                    <IonIcon icon={star} className="mr-2" />
                                    <span>Complete stats breakdown</span>
                                </div>
                                <div className="flex items-center text-[#0affff]">
                                    <IonIcon icon={trophy} className="mr-2" />
                                    <span>Compare with friends & pros</span>
                                </div>
                                <div className="flex items-center text-[#8FBC8F]">
                                    <IonIcon icon={trendingUp} className="mr-2" />
                                    <span>Track your progression</span>
                                </div>
                            </div>

                            <IonButton
                                onClick={handlePaywallClick}
                                className="w-full"
                                style={{
                                    '--background': 'linear-gradient(135deg, #5861AC 0%, #ff6ad5 100%)',
                                    '--border-radius': '12px',
                                    '--box-shadow': '0 4px 20px rgba(88, 97, 172, 0.4)',
                                    height: '48px',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Get Tow Rope Pass
                            </IonButton>

                            <p className="text-gray-500 text-xs mt-3">
                                Unlock advanced stats & comparisons
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Paywall-aware Stats Summary Card
    const PaywallStatsSummaryCard: React.FC<{
        stats: any;
        title: string;
        subtitle?: string;
        gradient?: string;
    }> = ({ stats, title, subtitle, gradient = 'from-purple-600 to-indigo-600' }) => {
        // If user has full stats access (subscribed OR under threshold), show all stats unlocked
        const shouldShowPaywall = !hasFullStatsAccess;

        const categories = [
            { key: 'rail', label: 'Rail/Jib', icon: '🛹', locked: false },
            { key: 'jump', label: 'Jump', icon: '🚀', locked: shouldShowPaywall },
            { key: 'style', label: 'Style', icon: '✨', locked: shouldShowPaywall },
            { key: 'off_snow', label: 'Off-Snow', icon: '💪', locked: shouldShowPaywall },
            { key: 'mountain', label: 'Freeride', icon: '⛰️', locked: shouldShowPaywall },
            { key: 'halfpipe', label: 'Halfpipe', icon: '🏂', locked: shouldShowPaywall }
        ];

        const visibleStats = categories.filter(cat => !cat.locked);
        const lockedStats = categories.filter(cat => cat.locked);

        const averageScore = hasFullStatsAccess
            ? Math.round(Object.values(stats).reduce((sum: number, val: any) => sum + val, 0) / Object.values(stats).length)
            : stats.rail || 0;

        return (
            <div className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${gradient} shadow-xl`}>
                <div className="absolute inset-0 bg-white/5 backdrop-blur-3xl"></div>

                <div className="relative z-10">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <h3 className="text-white font-bold text-lg">{title}</h3>
                            {subtitle && (
                                <p className="text-white/70 text-xs mt-1">{subtitle}</p>
                            )}
                        </div>
                        <div className="text-right">
                            <div className="text-white font-bold text-3xl">{averageScore}</div>
                            <div className="text-white/70 text-xs">
                                {hasFullStatsAccess ? 'Rating' : 'Rail/Jib'}
                            </div>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-3">
                        {/* Visible stats (rail/jib when not subscribed) */}
                        {visibleStats.map(category => (
                            <div
                                key={category.key}
                                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20"
                            >
                                <div className="text-2xl mb-1">{category.icon}</div>
                                <div className="text-white font-bold text-lg">
                                    {stats[category.key]}
                                </div>
                                <div className="text-white/70 text-xs font-medium">
                                    {category.label}
                                </div>
                            </div>
                        ))}

                        {/* Locked stats with mini paywall */}
                        {lockedStats.map(category => (
                            <div
                                key={category.key}
                                className="relative bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10 cursor-pointer hover:bg-white/10 transition-all"
                                onClick={handleLockedStatClick}
                            >
                                {/* Blurred background stat */}
                                <div className="blur-sm opacity-30">
                                    <div className="text-2xl mb-1">{category.icon}</div>
                                    <div className="text-white font-bold text-lg">
                                        {Math.floor(Math.random() * 100)}
                                    </div>
                                    <div className="text-white/70 text-xs font-medium">
                                        {category.label}
                                    </div>
                                </div>

                                {/* Lock overlay */}
                                <div className="absolute inset-0 flex flex-col justify-center items-center bg-black/40 backdrop-blur-sm rounded-xl">
                                    <IonIcon icon={lockClosed} className="text-white/80 text-lg mb-1" />
                                    <span className="text-white/60 text-xs font-medium">Pro</span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Call to action for non-subscribers at or above threshold - show paywall CTA */}
                    {/* For users under threshold, show how many free stats views remain */}
                    {!state.isSubscribed && totalClipCount >= FREE_STATS_THRESHOLD && (
                        <div className="mt-4 pt-3 border-t border-white/20">
                            <button
                                onClick={handlePaywallClick}
                                className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-2 border border-white/20 transition-all text-center"
                            >
                                <span className="text-white text-sm font-medium">
                                    🔓 Unlock your stats • Uploads always free
                                </span>
                            </button>
                        </div>
                    )}
                    {!state.isSubscribed && totalClipCount > 0 && totalClipCount < FREE_STATS_THRESHOLD && (
                        <div className="mt-4 pt-3 border-t border-white/20">
                            <div className="w-full bg-white/10 backdrop-blur-sm rounded-lg p-2 border border-white/20 text-center">
                                <span className="text-white/70 text-sm">
                                    ✨ Free stats for {FREE_STATS_THRESHOLD - totalClipCount} more clip{FREE_STATS_THRESHOLD - totalClipCount === 1 ? '' : 's'} • Uploads always free
                                </span>
                            </div>
                        </div>
                    )}
                    {!state.isSubscribed && totalClipCount === 0 && (
                        <div className="mt-4 pt-3 border-t border-white/20">
                            <button
                                onClick={handleUploadClick}
                                className="w-full bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-2 border border-white/20 transition-all text-center"
                            >
                                <span className="text-white text-sm font-medium">
                                    🎬 Upload a clip to build your stats
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Memoize Stats Header Container (inline filters)
    const StatsHeaderContainer = useMemo(() => (
        <div className="flex flex-row items-center justify-between">
            <div>
                <IonSelect
                    className="w-full max-w-xs"
                    aria-label="Sport"
                    color="theme-alternative"
                    interface="popover"
                    placeholder="Sport"
                    value={selectedSport}
                    onIonChange={(e) => handleSportChange(e.detail.value)}
                >
                    <IonSelectOption value={SportType.SKIING}>Skiing</IonSelectOption>
                    <IonSelectOption value={SportType.SNOWBOARDING}>Snowboarding</IonSelectOption>
                </IonSelect>
            </div>
            <div>
                <IonSelect
                    className="w-full max-w-xs"
                    aria-label="Period"
                    color="theme-alternative"
                    interface="popover"
                    placeholder="Period"
                    value={selectedPeriod}
                    onIonChange={(e) => handlePeriodChange(e.detail.value)}
                >
                    <IonSelectOption value={HotType.Week}>Last Week</IonSelectOption>
                    <IonSelectOption value={HotType.Month}>Last Month</IonSelectOption>
                    <IonSelectOption value={HotType.Season}>Last Season</IonSelectOption>
                    <IonSelectOption value={HotType.Year}>Last Year</IonSelectOption>
                    <IonSelectOption value={HotType.All}>All Time</IonSelectOption>
                </IonSelect>
            </div>
        </div>
    ), [selectedSport, selectedPeriod, handleSportChange, handlePeriodChange]);

    return (
        <IonPage>

            <CustomHelmet
                title="Lifty AI - Performance Stats"
                description="AI-powered performance analytics for skiers and snowboarders across different time periods"
                url="/stats"
            />
            <ToolbarSearchBarStats />
            <IonContent fullscreen className="bg-black">
                <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
                    <IonRefresherContent></IonRefresherContent>
                </IonRefresher>

                <div
                    className="bg-black"
                    style={{
                        height: '100%',
                        overflow: 'auto',
                        willChange: 'transform',
                        WebkitOverflowScrolling: 'touch'
                    }}
                >
                    <div className="min-h-full pb-8 bg-black">

                        {isLoading || !primaryUserStats ? (
                            <StatsLoadingSkeleton />
                        ) : (
                            <>
                                {/* Spider Chart Section */}
                                <div className="px-4 pt-2 pb-4">
                                    <PaywallOverlay className="relative">
                                        <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 shadow-2xl border border-gray-700">
                                            <div>
                                                {StatsHeaderContainer}
                                            </div>

                                            <SpiderChart
                                                userStats={chartData()}
                                                showLegend={true}
                                                height={375}
                                            />

                                            {/* Floating Add Comparison Button - only show if subscribed or disable if not */}
                                            <button
                                                onClick={handleOpenComparisonModal}
                                                className="absolute bottom-2 right-2 bg-[#FFFFFF] hover:bg-[#324E80] text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 flex items-center justify-center"
                                                aria-label="Add users to compare"
                                            >
                                                <IonIcon
                                                    icon={state.isSubscribed ? addCircle : lockClosed}
                                                    className="text-2xl text-[#000000]"
                                                />
                                                {state.isSubscribed && comparisonUsers.length > 0 && (
                                                    <span className="absolute -top-1 -right-1 bg-[#ff6ad5] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                                        {comparisonUsers.length}
                                                    </span>
                                                )}
                                            </button>
                                        </div>
                                    </PaywallOverlay>
                                </div>

                                {/* User Comparison Modal - Only for subscribers */}
                                <IonModal
                                    isOpen={showComparisonModal && state.isSubscribed}
                                    onDidDismiss={handleCloseComparisonModal}
                                    initialBreakpoint={0.75}
                                    breakpoints={[0, 0.5, 0.75, 1]}
                                >
                                    <div className="bg-gradient-to-br from-gray-900 to-gray-800 h-full">
                                        <div className="sticky top-0 bg-gradient-to-br from-gray-900 to-gray-800 border-b border-gray-700 p-4 z-10">
                                            <div className="flex items-center justify-between mb-2">
                                                <h2 className="text-white font-bold text-xl">
                                                    Compare with Others
                                                </h2>
                                                <IonButton
                                                    fill="clear"
                                                    size="default"
                                                    onClick={handleCloseComparisonModal}
                                                    style={{ color: '#FFFFFF' }}
                                                >
                                                    Done
                                                </IonButton>
                                            </div>
                                            <p className="text-gray-300 text-sm">
                                                Select up to 4 {selectedSport.toLowerCase()} riders to compare your stats
                                            </p>
                                        </div>

                                        <div className="p-4">
                                            <UserComparisonSelector
                                                currentUserId={state.user.id}
                                                selectedUsers={comparisonUsers}
                                                onUsersChange={handleComparisonUsersChange}
                                                maxUsers={4}
                                                sport={selectedSport}
                                            />
                                        </div>
                                    </div>
                                </IonModal>

                                {/* Stats Summary Card */}
                                <div className="px-4 pb-4">
                                    <SwipableStatsCard
                                        stats={primaryUserStats.stats}
                                        weeklyData={weeklyChartData}
                                        title={`Your ${selectedSport} Stats`}
                                        subtitle={`${selectedPeriod === HotType.All ? 'All Time' :
                                            selectedPeriod === HotType.Season ? 'Last Season' :
                                                selectedPeriod === HotType.Year ? 'Last Year' :
                                                    selectedPeriod === HotType.Month ? 'Last Month' :
                                                        'Last Week'}`}
                                        gradient="from-[#5861AC] to-[#324E80]"
                                        isSubscribed={hasFullStatsAccess}
                                        onPaywallClick={totalClipCount === 0 ? handleUploadClick : handlePaywallClick}
                                    />
                                </div>

                                {/* Comparison Stats Cards - Only for subscribers */}
                                {state.isSubscribed && comparisonStats?.comparison_users && comparisonStats.comparison_users.length > 0 && (
                                    <div className="px-4 space-y-4">
                                        {comparisonStats.comparison_users.map((user, index) => {
                                            const gradients = [
                                                'from-[#ff6ad5] to-[#CD5C5C]',
                                                'from-[#8FBC8F] to-[#5861AC]',
                                                'from-[#0affff] to-[#324E80]',
                                                'from-[#324E80] to-[#5861AC]'
                                            ];
                                            return (
                                                <StatsSummaryCard
                                                    key={user.user_id}
                                                    stats={user.stats}
                                                    title={`${user.username}'s ${selectedSport} Stats`}
                                                    subtitle={`Comparing ${selectedPeriod === HotType.All ? 'All Time' :
                                                        selectedPeriod === HotType.Season ? 'Last Season' :
                                                            selectedPeriod === HotType.Year ? 'Last Year' :
                                                                selectedPeriod === HotType.Month ? 'Last Month' :
                                                                    'Last Week'}`}
                                                    gradient={gradients[index % gradients.length]}
                                                />
                                            );
                                        })}
                                    </div>
                                )}

                                {/* Enhanced Section for Non-Subscribers - Show different CTAs based on clip count */}
                                {!state.isSubscribed && (
                                    <div className="px-4 pb-4">
                                        {totalClipCount === 0 ? (
                                            // Upload CTA for users with 0 clips
                                            <div className="bg-gradient-to-br from-[#8FBC8F]/20 to-[#5861AC]/20 backdrop-blur-sm rounded-2xl p-6 border border-[#8FBC8F]/30 shadow-2xl">
                                                <div className="text-center mb-6">
                                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#8FBC8F] to-[#5861AC] rounded-full mb-4">
                                                        <IonIcon icon={videocam} className="text-3xl text-white" />
                                                    </div>

                                                    <h2 className="text-white font-bold text-2xl mb-2">
                                                        Get Started with Lifty AI
                                                    </h2>

                                                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                                                        Upload your first clip and unlock AI-powered analysis of your riding!
                                                    </p>
                                                </div>

                                                <div className="grid grid-cols-1 gap-3 mb-6">
                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={cloudUpload} className="text-[#8FBC8F] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">Upload Any Clip</p>
                                                            <p className="text-gray-400 text-xs">Ski or snowboard, any terrain works</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={star} className="text-[#0affff] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">Get AI Analysis</p>
                                                            <p className="text-gray-400 text-xs">Lifty AI rates your skills automatically</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={trendingUp} className="text-[#ff6ad5] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">Build Your Stats</p>
                                                            <p className="text-gray-400 text-xs">Track progression as you upload more</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <IonButton
                                                    onClick={handleUploadClick}
                                                    expand="block"
                                                    className="mb-3"
                                                    style={{
                                                        '--background': 'linear-gradient(135deg, #8FBC8F 0%, #5861AC 50%, #0affff 100%)',
                                                        '--border-radius': '16px',
                                                        '--box-shadow': '0 8px 30px rgba(143, 188, 143, 0.4)',
                                                        height: '56px',
                                                        fontSize: '18px',
                                                        fontWeight: 'bold',
                                                        '--color': 'white'
                                                    }}
                                                >
                                                    🎬 Upload a Clip
                                                </IonButton>

                                                <div className="text-center">
                                                    <p className="text-gray-400 text-xs">
                                                        It only takes a few seconds!
                                                    </p>
                                                </div>
                                            </div>
                                        ) : totalClipCount < FREE_STATS_THRESHOLD ? (
                                            // Soft upsell for users under the threshold - they have full access but show value
                                            <div className="bg-gradient-to-br from-[#5861AC]/20 to-[#8FBC8F]/20 backdrop-blur-sm rounded-2xl p-6 border border-[#5861AC]/30 shadow-2xl">
                                                <div className="text-center mb-6">
                                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#5861AC] to-[#8FBC8F] rounded-full mb-4">
                                                        <IonIcon icon={star} className="text-3xl text-white" />
                                                    </div>

                                                    <h2 className="text-white font-bold text-2xl mb-2">
                                                        Keep Uploading! 🔥
                                                    </h2>

                                                    <p className="text-gray-300 text-sm leading-relaxed mb-2">
                                                        Upload as many clips as you want - it's always free! You can view your stats for {FREE_STATS_THRESHOLD - totalClipCount} more clip{FREE_STATS_THRESHOLD - totalClipCount === 1 ? '' : 's'}.
                                                    </p>

                                                    {/* Progress bar */}
                                                    <div className="w-full bg-white/10 rounded-full h-2 mb-4">
                                                        <div
                                                            className="bg-gradient-to-r from-[#8FBC8F] to-[#5861AC] h-2 rounded-full transition-all duration-500"
                                                            style={{ width: `${(totalClipCount / FREE_STATS_THRESHOLD) * 100}%` }}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 gap-3 mb-6">
                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={cloudUpload} className="text-[#8FBC8F] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">Unlimited Uploads</p>
                                                            <p className="text-gray-400 text-xs">Upload as many clips as you want, forever free</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={trophy} className="text-[#ff6ad5] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">Compare with Friends</p>
                                                            <p className="text-gray-400 text-xs">Unlock comparisons with Tow Rope Pass</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={trendingUp} className="text-[#8FBC8F] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">Unlimited Stats Access</p>
                                                            <p className="text-gray-400 text-xs">View your stats no matter how many clips you upload</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <IonButton
                                                    onClick={handlePaywallClick}
                                                    expand="block"
                                                    className="mb-3"
                                                    style={{
                                                        '--background': 'linear-gradient(135deg, #5861AC 0%, #ff6ad5 50%, #0affff 100%)',
                                                        '--border-radius': '16px',
                                                        '--box-shadow': '0 8px 30px rgba(88, 97, 172, 0.4)',
                                                        height: '56px',
                                                        fontSize: '18px',
                                                        fontWeight: 'bold',
                                                        '--color': 'white'
                                                    }}
                                                >
                                                    🚀 Get Unlimited Stats Access
                                                </IonButton>

                                                <div className="text-center">
                                                    <p className="text-gray-400 text-xs">
                                                        Uploads always free • Cancel anytime
                                                    </p>
                                                </div>
                                            </div>
                                        ) : (
                                            // Paywall for users at or above threshold
                                            <div className="bg-gradient-to-br from-[#5861AC]/20 to-[#ff6ad5]/20 backdrop-blur-sm rounded-2xl p-6 border border-[#5861AC]/30 shadow-2xl">
                                                <div className="text-center mb-6">
                                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#5861AC] to-[#ff6ad5] rounded-full mb-4">
                                                        <IonIcon icon={trophy} className="text-3xl text-white" />
                                                    </div>

                                                    <h2 className="text-white font-bold text-2xl mb-2">
                                                        Unlock Your Stats! 🎉
                                                    </h2>

                                                    <p className="text-gray-300 text-sm leading-relaxed mb-4">
                                                        You've uploaded {totalClipCount} clips - keep uploading, it's always free! Upgrade to view your detailed stats and track your progression.
                                                    </p>
                                                </div>

                                                <div className="grid grid-cols-1 gap-3 mb-6">
                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={cloudUpload} className="text-[#8FBC8F] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">Unlimited Uploads Forever</p>
                                                            <p className="text-gray-400 text-xs">Keep uploading clips - always 100% free</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={star} className="text-[#ff6ad5] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">View All Your Stats</p>
                                                            <p className="text-gray-400 text-xs">Track all 6 skill categories in detail</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center bg-black/30 rounded-lg p-3">
                                                        <IonIcon icon={trophy} className="text-[#0affff] text-xl mr-3 flex-shrink-0" />
                                                        <div>
                                                            <p className="text-white font-medium text-sm">Compare with Anyone</p>
                                                            <p className="text-gray-400 text-xs">See how you stack up against friends & pros</p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <IonButton
                                                    onClick={handlePaywallClick}
                                                    expand="block"
                                                    className="mb-3"
                                                    style={{
                                                        '--background': 'linear-gradient(135deg, #5861AC 0%, #ff6ad5 50%, #0affff 100%)',
                                                        '--border-radius': '16px',
                                                        '--box-shadow': '0 8px 30px rgba(88, 97, 172, 0.4)',
                                                        height: '56px',
                                                        fontSize: '18px',
                                                        fontWeight: 'bold',
                                                        '--color': 'white'
                                                    }}
                                                >
                                                    🚀 Unlock My Stats
                                                </IonButton>

                                                <div className="text-center">
                                                    <p className="text-gray-400 text-xs">
                                                        Uploads always free • Cancel anytime
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Info Section */}
                                <div className="px-4 pb-4">
                                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
                                        <p className="text-gray-400 text-xs leading-relaxed">
                                            <span className="font-semibold text-gray-300">How it works:</span> Your stats are calculated based on Lifty AI analysis across all of your clips, lines, and edits.
                                            We call them "Smoke Stats". The chart shows your relative strengths across six categories of the ski/snowboard universe. Style is only calculated based off Edits. Lines only affect your Freeride score.
                                            Tag your clips to help point Lifty AI in the right direction! Clips without a sport or that are unlisted will not be included in your stats.
                                        </p>
                                    </div>
                                </div>
                                <div className="px-4 pb-4">
                                    <div className="bg-gray-900/50 rounded-xl p-4 border border-gray-800">
                                        <p className="text-gray-400 text-xs leading-relaxed">
                                            Stats are updated every 15 minutes.
                                        </p>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <IonToast
                    isOpen={showError}
                    onDidDismiss={() => setShowError(false)}
                    message={errorMessage}
                    duration={3000}
                    color="danger"
                    position="top"
                />
            </IonContent>
        </IonPage>
    );
};

export default StatsPage;
